/**
 * Global Command Palette (⌘K / Ctrl+K)
 * Searches the whole operational spine — customers, contacts, properties,
 * leads, projects, installers and appointments — through one bounded,
 * staff-only server function. No new search layer.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem,
} from "@/components/ui/command";
import { Users, Briefcase, User, Home, Wrench, CalendarDays, Contact, Search } from "lucide-react";

import { Button } from "@/components/ui/button";

 type EntityType = "customer" | "contact" | "property" | "lead" | "job" | "installer" | "appointment";

export interface SearchResult {
  entity_type: EntityType;
  entity_id: string;
  label: string;
  sublabel: string | null;
  href: string;
}

const GROUPS: { type: EntityType; heading: string; icon: typeof Users }[] = [
  { type: "customer", heading: "Customers", icon: User },
  { type: "contact", heading: "Contacts", icon: Contact },
  { type: "property", heading: "Properties", icon: Home },
  { type: "lead", heading: "Leads", icon: Users },
  { type: "job", heading: "Projects", icon: Briefcase },
  { type: "installer", heading: "Installers", icon: Wrench },
  { type: "appointment", heading: "Appointments", icon: CalendarDays },
];

export default function CommandPalette({ onPick, allowedTypes }: { onPick?: (result: SearchResult) => void; allowedTypes?: EntityType[] } = {}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // ⌘K / Ctrl+K
  useEffect(() => {
    if (onPick) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onPick]);

  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    setResults([]);
    setError(null);
    setSearching(open && q.length >= 2);
    if (!open || q.length < 2) return;
    const timer = setTimeout(async () => {
      try {
        const { data, error: rpcError } = await supabase.rpc("global_operational_search", { p_query: q, p_limit: 6 });
        if (cancelled) return;
        if (rpcError) {
          setError(rpcError.code === "42501" ? "Your account does not have access to operational search." : "Search could not load. Please try again.");
        } else {
          setResults((data ?? []) as SearchResult[]);
        }
      } catch {
        if (!cancelled) setError("Search could not load. Please check your connection and try again.");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, open]);

  const select = (href: string) => {
    setOpen(false);
    setQuery("");
    navigate(href);
  };

  return (
    <>
    <Button type="button" variant="ghost" className="min-h-[44px] min-w-[44px] gap-2" aria-label={onPick ? "Choose contact, enquiry or project" : "Search workspace"} onClick={() => setOpen(true)}>
      <Search className="h-4 w-4" /><span className={onPick ? "" : "hidden sm:inline"}>{onPick ? "Choose contact, enquiry or project" : "Search"}</span>
    </Button>
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Search customers, contacts, addresses, leads, projects, installers…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {error
            ? error
            : searching
              ? "Searching…"
              : query.trim().length < 2
                ? "Type a name, telephone number, address or postcode…"
                : "No results found."}
        </CommandEmpty>

        {GROUPS.map(({ type, heading, icon: Icon }) => {
          const group = results.filter((r) => r.entity_type === type && (!allowedTypes || allowedTypes.includes(type)));
          if (group.length === 0) return null;
          return (
            <CommandGroup key={type} heading={heading}>
              {group.map((r) => (
                <CommandItem
                  key={`${type}-${r.entity_id}`}
                  value={`${heading} ${r.label} ${r.sublabel ?? ""} ${r.entity_id}`}
                  onSelect={() => { if (onPick) { setOpen(false); setQuery(""); onPick(r); } else select(r.href); }}
                  className="gap-3"
                >
                  <Icon className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{r.label}</p>
                    {r.sublabel && (
                      <p className="text-xs text-muted-foreground truncate">{r.sublabel}</p>
                    )}
                  </div>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70 shrink-0">
                    {heading.replace(/s$/, "")}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </CommandDialog>
    </>
  );
}
